import React, { useState, useEffect } from 'react';
import UserManagement from './UserManagement';

const AdminTools = ({ apiEndpoints }) => {
  // Keep debugging code but hide it from UI
  console.log('AdminTools received apiEndpoints:', apiEndpoints);
  
  const [activeTab, setActiveTab] = useState('users');
  const [debugInfo, setDebugInfo] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [resolvedEndpoints, setResolvedEndpoints] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false); // Control debug visibility

  // More flexible check for API endpoints that handles both camelCase and snake_case
  const hasApiEndpoints = (() => {
    if (resolvedEndpoints) return true;
    
    if (!apiEndpoints || typeof apiEndpoints !== 'object') return false;
    
    // Check for users endpoint
    const hasUsers = Boolean(
      apiEndpoints.users
    );
    
    // Check for toggle_admin endpoint (either camelCase or snake_case)
    const hasToggleAdmin = Boolean(
      apiEndpoints.toggleAdmin || apiEndpoints.toggle_admin
    );
    
    return hasUsers && hasToggleAdmin;
  })();
  
  // Function to normalize the API endpoints (handle both camelCase and snake_case)
  const normalizeApiEndpoints = () => {
    if (resolvedEndpoints) return resolvedEndpoints;
    
    if (!apiEndpoints || typeof apiEndpoints !== 'object') return null;
    
    // Create a normalized version with camelCase keys
    return {
      users: apiEndpoints.users,
      toggleAdmin: apiEndpoints.toggleAdmin || apiEndpoints.toggle_admin,
      icdCodes: apiEndpoints.icdCodes || apiEndpoints.icd_codes,
      uploadIcd: apiEndpoints.uploadIcd || apiEndpoints.upload_icd,
      refreshIcd: apiEndpoints.refreshIcd || apiEndpoints.refresh_icd
    };
  };
  
  // Normalized API endpoints for child components
  const normalizedApiEndpoints = normalizeApiEndpoints();

  // Function to attempt a direct fetch of users as a last resort (hidden from UI but kept for potential future debugging)
  const attemptDirectUsersFetch = async () => {
    setDebugLoading(true);
    try {
      console.log('Attempting direct users fetch...');
      
      // Get a CSRF token if available
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      // Attempt to fetch from the /admin/users endpoint directly
      const response = await fetch('/admin/users', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || '',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Direct users fetch successful:', data);
      
      // Create a minimal apiEndpoints object and set it as the resolved endpoints
      const minimalApiEndpoints = {
        users: '/admin/users',
        toggleAdmin: '/admin/users/:id/toggle_admin'
      };
      
      console.log('Setting minimal resolved endpoints:', minimalApiEndpoints);
      setResolvedEndpoints(minimalApiEndpoints);
      
      // Update the active tab to show the users tab
      setActiveTab('users');
      
    } catch (error) {
      console.error('Direct users fetch failed:', error);
      // Silent error handling for hidden function
    } finally {
      setDebugLoading(false);
    }
  };

  // Function to fetch debug info and use its endpoints
  const fetchDebugInfo = async () => {
    setDebugLoading(true);
    try {
      const response = await fetch('/api_debug', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('API debug info:', data);
      setDebugInfo(data);
      
      // If we have valid api_endpoints in the debug data, use them to create a resolved set
      if (data && data.api_endpoints && data.api_endpoints.users) {
        const newResolvedEndpoints = {
          users: data.api_endpoints.users,
          toggleAdmin: data.api_endpoints.toggle_admin,
          icdCodes: data.api_endpoints.icd_codes,
          uploadIcd: data.api_endpoints.upload_icd,
          refreshIcd: data.api_endpoints.refresh_icd
        };
        console.log('Setting resolved endpoints from debug data:', newResolvedEndpoints);
        setResolvedEndpoints(newResolvedEndpoints);
      }
    } catch (err) {
      console.error('Error fetching API debug info:', err);
    } finally {
      setDebugLoading(false);
    }
  };

  useEffect(() => {
    // Always fetch debug info when component mounts
    fetchDebugInfo();
    
    // Set a timer to refresh debug info every few seconds if needed
    if (!hasApiEndpoints) {
      const timer = setTimeout(() => {
        console.log('Refreshing debug info to try to resolve endpoints...');
        fetchDebugInfo();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [hasApiEndpoints]);

  // Setup keyboard shortcut for debug panel (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        console.log('Debug mode triggered via keyboard shortcut');
        setShowDebugPanel(prev => !prev);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const renderTabContent = () => {
    // If we have resolved endpoints from debug info, use those
    if (resolvedEndpoints) {
      switch (activeTab) {
        case 'users':
          return <UserManagement apiEndpoints={resolvedEndpoints} />;
        case 'system':
          return (
            <div className="p-4 border rounded bg-light">
              <h4>System Settings</h4>
              <p className="text-muted">System configuration options will be available here in future updates.</p>
            </div>
          );
        default:
          return <div>Select a tab</div>;
      }
    }
    
    // If apiEndpoints is missing, show an error message with debug info
    if (!hasApiEndpoints) {
      return (
        <div>
          <div className="alert alert-danger">
            <h4 className="alert-heading">Configuration Error</h4>
            <p>
              The API endpoints configuration is missing or invalid. This is required for the Admin Tools to function properly.
            </p>
            <hr />
            <p className="mb-0">
              Please contact the system administrator to resolve this issue.
            </p>
          </div>
        </div>
      );
    }

    // Otherwise, render the selected tab content using normalized endpoints
    switch (activeTab) {
      case 'users':
        return <UserManagement apiEndpoints={normalizedApiEndpoints} />;
      case 'system':
        return (
          <div className="p-4 border rounded bg-light">
            <h4>System Settings</h4>
            <p className="text-muted">System configuration options will be available here in future updates.</p>
          </div>
        );
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="admin-tools-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Admin Tools</h2>
        <div className="text-muted small">
          <span 
            className="badge bg-light text-secondary border d-inline-flex align-items-center" 
            title="Press Ctrl+Shift+D to access debug mode"
            style={{ cursor: 'help' }}
          >
            <i className="fas fa-keyboard me-1"></i>
            <span className="d-none d-sm-inline">Debug: Ctrl+Shift+D</span>
            <span className="d-inline d-sm-none">Ctrl+Shift+D</span>
          </span>
        </div>
      </div>
      
      <div className="admin-tabs mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <i className="fas fa-users me-2"></i> User Management
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'system' ? 'active' : ''}`}
              onClick={() => setActiveTab('system')}
            >
              <i className="fas fa-cogs me-2"></i> System Settings
            </button>
          </li>
        </ul>
      </div>
      
      {/* Hidden Debug Panel - Only visible when showDebugPanel is true */}
      {showDebugPanel && (
        <div className="card mb-4 border-warning">
          <div className="card-header bg-warning text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-tools me-2"></i>
              Admin Debug Panel
            </h5>
            <button 
              className="btn btn-sm btn-outline-dark" 
              onClick={() => setShowDebugPanel(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="card-body">
            <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle me-2"></i>
              This panel is for administrator troubleshooting only. It provides advanced diagnostics and direct API access for fixing issues with the Admin Tools.
            </div>
            <div className="row">
              <div className="col-md-6">
                <h6>Actions</h6>
                <div className="btn-group mb-3">
                  <button 
                    className="btn btn-sm btn-outline-primary" 
                    onClick={fetchDebugInfo}
                    disabled={debugLoading}
                  >
                    <i className="fas fa-sync-alt me-1"></i> Refresh Debug Info
                  </button>
                  <button 
                    className="btn btn-sm btn-outline-danger" 
                    onClick={attemptDirectUsersFetch}
                    disabled={debugLoading}
                  >
                    <i className="fas fa-bolt me-1"></i> Direct API Fetch
                  </button>
                </div>
                
                <h6>State Information</h6>
                <ul className="list-group mb-3">
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Has API Endpoints
                    <span className={`badge ${hasApiEndpoints ? 'bg-success' : 'bg-danger'}`}>
                      {hasApiEndpoints ? 'Yes' : 'No'}
                    </span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Active Tab
                    <span className="badge bg-primary">{activeTab}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Has Resolved Endpoints
                    <span className={`badge ${resolvedEndpoints ? 'bg-success' : 'bg-secondary'}`}>
                      {resolvedEndpoints ? 'Yes' : 'No'}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="col-md-6">
                <h6>Debug Info</h6>
                {debugLoading ? (
                  <div className="d-flex align-items-center p-3">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <span>Loading...</span>
                  </div>
                ) : (
                  <pre className="p-2 bg-light" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.8rem' }}>
                    {debugInfo ? JSON.stringify(debugInfo, null, 2) : 'No debug info available'}
                  </pre>
                )}
                
                <h6 className="mt-3">API Endpoints</h6>
                <pre className="p-2 bg-light" style={{ maxHeight: '150px', overflow: 'auto', fontSize: '0.8rem' }}>
                  {apiEndpoints ? JSON.stringify(apiEndpoints, null, 2) : 'No API endpoints'}<br/>
                  {resolvedEndpoints ? `\nResolved: ${JSON.stringify(resolvedEndpoints, null, 2)}` : ''}
                </pre>
              </div>
            </div>
            
            <div className="text-muted mt-2 small">
              <i className="fas fa-info-circle me-1"></i> Press Ctrl+Shift+D to toggle this debug panel
            </div>
          </div>
        </div>
      )}
      
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminTools; 
